import React, { useEffect, useRef } from 'react';
import { getBehavior, useFramework } from '@/framework';
import { type IPoint } from '@/version-types';
import { getAdapter } from '../adapter';
import { TEXT_EDIT_EVENTS } from '../constants';
import { useTextEditState } from '../states/text-edit-state';
import TextEditArea, { type Ref as TextEditAreaRef } from './text-edit-area';
import './style.less';

const TextEditWidget: React.FC = () => {
    // --- system hooks ---
    const framework = useFramework();

    // --- store hooks ---
    const editingRange = useTextEditState()((state) => state.editingRange);
    const layer = useTextEditState()((state) => state.editingLayer);
    const getEditingLayer = useTextEditState()((state) => state.getEditingLayer);
    const setEditingLayer = useTextEditState()((state) => state.setEditingLayer);
    const getOriginLayer = useTextEditState()((state) => state.getOriginLayer);
    const setOriginLayer = useTextEditState()((state) => state.setOriginLayer);
    const enterEditing = useTextEditState()((state) => state.enterEditing);
    const exitEditing = useTextEditState()((state) => state.exitEditing);

    const hasEditingLayer = !!layer;

    // --- internal refs ---
    const editAreaRef = useRef<TextEditAreaRef | null>(null);

    // listen double click and click event
    useEffect(() => {
        const handler = async (layerID: string, point: IPoint, way: 'click' | 'doubleClick') => {
            const layer = framework.getLayer(layerID, true);
            if (!layer) return;

            const behavior = getBehavior(framework.identity, layer);
            const isTextEditable = (() => {
                if (!behavior || !behavior.isTextEditable) return false;
                return behavior.isTextEditable();
            })();
            const adapter = getAdapter(layer.layerType);
            const isRegisteredAdapter = adapter != null;

            const editingLayer = getEditingLayer();

            if (!isTextEditable || !isRegisteredAdapter) {
                if (editingLayer) exitEditing();
                return;
            }

            const supportedWays = adapter.getEnterEditingModeWays();
            if (!supportedWays.includes(way)) return;

            if (!editingLayer) return enterEditing(layerID, point);

            if (editingLayer.id !== layer.id) {
                await exitEditing();
                enterEditing(layer.id, point);
            }
        };

        const dbClickListenerID = framework.events.onLayerDoubleClick(
            (layerID, point) => handler(layerID, point, 'doubleClick'),
            9,
        );
        const clickListenerID = framework.events.onLayerClick((layerID, point) => handler(layerID, point, 'click'));

        return () => {
            framework.events.off(dbClickListenerID);
            framework.events.off(clickListenerID);
        };
    }, []);

    // listen layer update
    useEffect(() => {
        const listenerID = framework.events.onActiveLayerDataUpdate(async (activeLayer, pickLayer) => {
            const layer = pickLayer || activeLayer;
            const editingLayer = getEditingLayer();

            if (!layer) {
                if (editingLayer) exitEditing();
                return;
            }

            const behavior = getBehavior(framework.identity, layer);
            const isTextEditable = (() => {
                if (!behavior || !behavior.isTextEditable) return false;
                return behavior.isTextEditable();
            })();
            const adapter = getAdapter(layer.layerType);
            const isRegisteredAdapter = adapter != null;

            if (!isTextEditable || !isRegisteredAdapter) {
                if (editingLayer) exitEditing();
                return;
            }

            if (!editingLayer) return;

            if (editingLayer.id !== layer.id) return exitEditing();

            if (layer.lock) return exitEditing();

            // update target layer
            // ATTENTION: 由于图层属性改变写入历史记录时会将 `__isOnTextEditing` 标记去除，所以这里需要设置 `__isOnTextEditing` 标记保证状态正确
            setEditingLayer({ ...layer, __isOnTextEditing: true });
        });

        return () => {
            framework.events.off(listenerID);
        };
    }, []);

    // 发送 range 变化事件
    useEffect(() => {
        framework.events.emit(TEXT_EDIT_EVENTS.RANGE_CHANGE, editingRange);
    }, [editingRange]);

    // 监听图层 移动/旋转 事件
    useEffect(() => {
        const listenerID = framework.events.onTranslate((layerID) => {
            const editingLayer = getEditingLayer();
            if (!editingLayer) return;
            if (editingLayer.id !== layerID) return;

            const node = editAreaRef.current?.$layerViewWrapper;
            if (!node) return;

            const layer = framework.getLayer(layerID);
            const { left, top, rotate } = layer;

            node.style.transform = `translate(${left}px, ${top}px) rotate(${rotate}deg)`;
        });

        return () => {
            framework.events.off(listenerID);
        };
    }, []);

    // 监听历史 onBeforeChange，退出编辑态
    // Q: 为什么有该逻辑？
    // A: 主要是处理用户手动点击历史记录前进/后退时，需要主动退出编辑态
    //    同时需要注意要调用 `delActiveLayer`
    useEffect(() => {
        const listenerID = framework.history.onBeforeChange(() => {
            const editingLayer = getEditingLayer();
            if (!editingLayer) return;

            framework.operate.delActiveLayer();
            exitEditing();
        });

        return () => {
            framework.events.off(listenerID);
        };
    }, []);

    // 监听历史记录 onAfterWrite，更新原始图层数据
    // Q: 为什么有该逻辑？
    // A: 处于编辑态中，外部仍可以改变图层数据，此时需要同步最新数据
    //    否则在退出编辑态时，会重复写入历史记录
    useEffect(() => {
        if (!hasEditingLayer) return;

        const listenerID = framework.history.onAfterWrite(() => {
            const editingLayer = getEditingLayer();
            if (!editingLayer) return;

            const latest = framework.getLayer(editingLayer.id, true);
            const originLayer = getOriginLayer();
            if (latest && originLayer && latest.id === originLayer.id) setOriginLayer(latest);
        });

        return () => {
            framework.events.off(listenerID);
        };
    }, [hasEditingLayer]);

    if (!layer) return null;

    return <TextEditArea ref={editAreaRef} />;
};

export default TextEditWidget;
